import React, { useState, useEffect, useMemo } from "react";

import { useAppDispatch, useAppSelector } from "../../redux/hooks/redux-hooks";
import { fetchSegmentLOBs } from "../../redux/actions/SegmentLOBActions";
import { fetchCustomers } from "../../redux/actions/CustomerActions";
import { fetchCustomerGreenClassifications } from "../../redux/actions/CustomerGreenClassificationActions";
import { displayDateFormart, isValidArr } from "../../utility/utils";
import EnhancedTable from "../table/Table";
import PageTitle from "../PageTitle";
import PageSearch from "../PageSearch";
import DialogBox from "../../components/dialog-box/DialogBox";
import CustomerGreenClassificationForm from "./CustomerGreenClassificationForm";
import Loader from "../../components/loader/Loader";
import { CustomerGreenClassificationModel } from "../../models/CustomerGreenClassificationModel";

export type Mode = "add" | "delete";

type iData = {
  id: string;
  segmentLOB: string;
  customer: string;
  createdDate: string;
  // status: string;
};

const CustomerGreenClassificationPage = () => {
  const dispatch = useAppDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("add");
  const [editData, setEditData] = useState<CustomerGreenClassificationModel>();
  const [rootData, setRootData] = useState<iData[]>([]);
  const [filteredData, setFilteredData] = useState<iData[]>([]);

  const allCustomers = useAppSelector((state) => state.customer.all_Customers);
  const allSegmentLOBs = useAppSelector(
    (state) => state.segmentLOB.all_SegmentLOBs
  );
  const allCustomerGreenClassifications = useAppSelector(
    (state) =>
      state.customerGreenClassifications.all_Customer_Green_Classfications
  );

  const isCustomerGreenClassificationsFetching = useAppSelector(
    (state) =>
      state.customerGreenClassifications.isCustomerGreenClassificationsFetching
  );

  const columns = useMemo(() => {
    return [
      {
        id: "segmentLOB",
        numeric: false,
        disablePadding: false,
        label: "Segment LOB",
      },
      {
        id: "customer",
        numeric: false,
        disablePadding: false,
        label: "Customer",
      },
      // {
      //   id: "status",
      //   numeric: false,
      //   disablePadding: false,
      //   label: "Green Customer",
      //   width: 200,
      // },
      {
        id: "createdDate",
        numeric: false,
        disablePadding: false,
        label: "Created Date",
        width: 150,
      },
    ];
  }, []);

  //To fetch all Customers, SetgmentLOBs and customer green classifications
  useEffect(() => {
    dispatch(fetchSegmentLOBs());
    dispatch(fetchCustomers());
    dispatch(fetchCustomerGreenClassifications());
  }, [dispatch]);

  useEffect(() => {
    setRootData(() =>
      allCustomerGreenClassifications.map((rec) => {
        const segmentLOB = allSegmentLOBs.find(
          (segmentRec) => segmentRec.segmentlobid === rec.segmentLobid
        );
        const customer = allCustomers.find(
          (customerRec) => customerRec.customerId === rec.customerId
        );
        return {
          id: `${rec.segmentLobid}|${rec.customerId}`,
          segmentLOB: segmentLOB?.name!,
          customer: customer?.name!,
          createdDate: displayDateFormart(rec.createdDate),
          // status: rec.customerGreenClassification1 ? "Active" : "Inactive",
        };
      })
    );
  }, [allCustomerGreenClassifications, allSegmentLOBs, allCustomers]);

  // To set filtered data on change root data
  useEffect(() => {
    setFilteredData(rootData);
  }, [rootData]);

  const handleEdit = (id: string | number) => {
    const idArr = id.toString().split("|");
    const editRec = allCustomerGreenClassifications.find(
      (rec) => rec.segmentLobid === +idArr[0] && rec.customerId === +idArr[1]
    );
    setMode("delete");
    setEditData(editRec);
    setIsModalOpen(true);
  };

  const createHandler = () => {
    setMode("add");
    setEditData(undefined);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const searchHandler = (searchVal: string): void => {
    setFilteredData(() =>
      searchVal
        ? rootData.filter((info) =>
            Object.values(info).join("").toLowerCase().includes(searchVal)
          )
        : rootData
    );
  };

  return (
    <>
      {!isValidArr(allCustomerGreenClassifications) &&
      isCustomerGreenClassificationsFetching ? (
        <Loader />
      ) : null}
      <PageTitle
        title="Customer Green Classifications"
        btnText="Add Customer Green Classification"
        onClick={createHandler}
      />
      <PageSearch
        placeholder="Search Customer Green Classifications"
        searchHandler={searchHandler}
      />
      <EnhancedTable
        columns={columns}
        data={filteredData}
        handleEdit={handleEdit}
        isEditable={false}
      />
      {isModalOpen && (
        <DialogBox
          isModalOpen={isModalOpen}
          modalTitle={
            mode === "add"
              ? "Create Customer Green Classifiaction"
              : "Delete Customer Green Classifiaction"
          }
          handleModalClose={handleModalClose}
        >
          <CustomerGreenClassificationForm
            mode={mode}
            data={editData}
            handleModalClose={handleModalClose}
          />
        </DialogBox>
      )}
    </>
  );
};

export default CustomerGreenClassificationPage;
